import { useState, type ReactNode } from 'react'
import { SearchField, ToggleButton } from '@heroui/react'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Glyph } from './Editor'
import { IconButton } from './heroParts'
import type { LayoutElement } from './types'
import '../styles/studio.css'

/**
 * The editor's left panel: the screens (kiosk), a search over the components,
 * the components in their sections, and the layers at the bottom.
 */
export function LeftPanel({
  screens,
  children,
  layers,
}: {
  screens?: ReactNode
  /** The components, given what the search box holds. */
  children: (query: string) => ReactNode
  layers: ReactNode
}) {
  const { say } = useStaffText()
  const [query, setQuery] = useState('')
  return (
    <div className="studio-left">
      {screens && <div className="studio-left__screens">{screens}</div>}
      <SearchField className="studio-search-row" aria-label={say('designs.tools.search')} value={query} onChange={setQuery} variant="secondary" fullWidth>
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder={say('designs.tools.search')} />
          <SearchField.ClearButton />
        </SearchField.Group>
      </SearchField>
      <div className="studio-comps">{children(query)}</div>
      {layers}
    </div>
  )
}

export interface LayerRow {
  element: LayoutElement
  /** What the element says, or its kind's name when it says nothing of its own. */
  name: string
  /** Its kind's name. */
  kind: string
  /** The worst thing to fix about it, marked with a dot. */
  issue?: { text: string; blocks: boolean }
}

/** Every element drawn here, front first: picked on click, hidden or shown by its eye. */
export function LayerList({
  rows,
  picked,
  onSelect,
  onShiftSelect,
  onRestack,
  onToggleHidden,
}: {
  rows: readonly LayerRow[]
  picked: readonly string[]
  onSelect: (id: string) => void
  onShiftSelect: (id: string) => void
  onRestack: (id: string, dir: 1 | -1) => void
  onToggleHidden: (id: string) => void
}) {
  const { say } = useStaffText()
  return (
    <section className="studio-layersec" aria-labelledby="studio-layers-cap">
      <h3 id="studio-layers-cap" className="studio-comps__cap">
        {say('designs.page.layers')} <span className="studio-comps__n">{rows.length}</span>
      </h3>
      {rows.length === 0 ? (
        <p className="studio-layers__empty">{say('designs.page.layersEmpty')}</p>
      ) : (
        <ul className="studio-layers">
          {rows.map(({ element: el, name, kind, issue }, i) => {
            const on = picked.includes(el.id)
            const shown = name === kind ? kind : say('designs.layers.named', { kind, name })
            const eye = say(el.hidden ? 'designs.layers.show' : 'designs.layers.hide', { name })
            return (
              <li key={el.id} className="studio-layer" data-on={on || undefined} data-hidden={el.hidden || undefined}>
                <ToggleButton
                  variant="ghost"
                  size="sm"
                  className="studio-layer__pick"
                  isSelected={on}
                  onPress={(e) => (e.shiftKey ? onShiftSelect(el.id) : onSelect(el.id))}
                >
                  <Glyph of={el.kind} size={16} />
                  <span className="studio-layer__name" title={shown}>{shown}</span>
                  {issue && <span className="studio-layer__issue" data-blocks={issue.blocks || undefined} role="img" aria-label={issue.text} title={issue.text} />}
                </ToggleButton>
                <span className="studio-layer__tools">
                  <IconButton isDisabled={i === 0} onPress={() => onRestack(el.id, 1)} aria-label={say('designs.page.bringForward', { name })}>
                    <Icon name="up" size={14} />
                  </IconButton>
                  <IconButton isDisabled={i === rows.length - 1} onPress={() => onRestack(el.id, -1)} aria-label={say('designs.page.sendBack', { name })}>
                    <Icon name="down" size={14} />
                  </IconButton>
                </span>
                <IconButton className="studio-layer__eye" aria-pressed={!el.hidden} aria-label={eye} onPress={() => onToggleHidden(el.id)}>
                  <Icon name={el.hidden ? 'eyeOff' : 'eye'} size={15} />
                </IconButton>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
